import { Component, computed, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
	CustomRequisitionPrecedent,
	MatterRequisitionPrecedent,
} from '@models/requisitions';
import { createId } from '@core/util/file';
import { MatterRequisitionsDispatch } from '@state/matter/requisitions/matter-requisitions.dispatch';
import { SnackbarService } from '@services/ui/snackbar.service';
import { CreateStatusType } from '@state/utils/models';
import { Observable } from 'rxjs';
import { MatterRequisitionsState } from '@state/matter/requisitions/matter-requisitions.state';
import { BusinessRequisitionsState } from '@state/business/requisition/business_requisitions.state';
import { BusinessRequisitionsDispatch } from '@state/business/requisition/business_requisitions.dispatch';
import {
	BaseMatterRequisitionModal,
	IBaseMatterRequisitionModalData,
} from '../base-matter-requisition-modal';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { TestHooks } from './new-requisition-modal.hooks';
import { select } from '@ngxs/store';

enum RequisitionToggle {
	NEW = 'NEW',
	CUSTOM = 'CUSTOM',
}
export interface INewMatterRequisitionModalData
	extends IBaseMatterRequisitionModalData {
	fields?: Record<string, string>;
	toggle?: RequisitionToggle;
	searchTerm?: string;
}

@Component({
	selector: 'app-new-requisition-modal',
	templateUrl: './new-requisition-modal.component.html',
	standalone: false,
})
export class NewRequisitionModalComponent
	extends BaseMatterRequisitionModal<
		MatterRequisitionPrecedent | CustomRequisitionPrecedent
	>
	implements OnInit
{
	testHooks = TestHooks;
	private matterStatusState$ = this.store.select(
		MatterRequisitionsState.getCreateStatus,
	);
	private businessStatusState$ = this.store.select(
		BusinessRequisitionsState.getCreateStatus,
	);
	selector = MatterRequisitionsState.getRequisitionById;
	protected statusState$: Observable<CreateStatusType>[] = [];
	public isLoggedInUserAdmin_ = select(BusinessUsersState.isLoggedInUserAdmin);
	selectedRequisition: MatterRequisitionPrecedent;
	RequisitionToggle = RequisitionToggle;
	toggle: RequisitionToggle = RequisitionToggle.NEW;
	fields: Record<string, string> = {};
	searchTerm: string;
	baseRequisitions: Partial<MatterRequisitionPrecedent>[];

	constructor(
		@Inject(MAT_DIALOG_DATA) protected data: INewMatterRequisitionModalData,
		protected dialogRef: MatDialogRef<NewRequisitionModalComponent>,
		protected snackbarService: SnackbarService,
		private matterRequisitionsDispatch: MatterRequisitionsDispatch,
		private businessRequisitionDispatch: BusinessRequisitionsDispatch,
	) {
		super(data, dialogRef, snackbarService);
	}

	ngOnInit(): void {
		if (this.data?.toggle) {
			this.onToggleChange(this.data.toggle);
		}

		if (this.data?.fields) {
			this.fields = this.data.fields;
		}

		this.statusState$ = [this.matterStatusState$, this.businessStatusState$];
		if (this.data?.searchTerm && this.data.searchTerm.length > 0) {
			this.searchTerm = this.data.searchTerm;
		}

		super.ngOnInit();
	}

	toolTipText_ = computed(() =>
		this.isLoggedInUserAdmin_()
			? null
			: 'Contact an admin to save this as a new requisition template.',
	);

	get successMessage(): string {
		return this.toggle === RequisitionToggle.CUSTOM
			? 'Custom Requisition Template Added Successfully'
			: 'Requisition Added to Matter Successfully';
	}

	get errorMessage(): string {
		return this.toggle === RequisitionToggle.CUSTOM
			? 'Error adding custom requisition'
			: 'Error adding requisition to matter';
	}

	public onSubmitAction({
		id: businessRequisitionId,
		...requisition
	}: MatterRequisitionPrecedent | CustomRequisitionPrecedent):
		| MatterRequisitionPrecedent
		| CustomRequisitionPrecedent {
		const newRequisition = {
			...requisition,
			id: createId(10),
		};

		if (this.toggle === RequisitionToggle.CUSTOM) {
			this.businessRequisitionDispatch.Create(
				newRequisition as CustomRequisitionPrecedent,
			);
		} else {
			this.matterRequisitionsDispatch.Create({
				...newRequisition,
				extendedRequisitionId: businessRequisitionId,
			} as MatterRequisitionPrecedent);
		}

		return newRequisition;
	}

	public onToggleChange(toggle: RequisitionToggle) {
		this.toggle = toggle;
	}

	protected afterSuccessfulSubmit(
		requisition: MatterRequisitionPrecedent | CustomRequisitionPrecedent,
	) {
		if (this.toggle === RequisitionToggle.CUSTOM) {
			this.selectedRequisition = requisition as MatterRequisitionPrecedent;
			this.onToggleChange(RequisitionToggle.NEW);
		} else {
			this.dialogRef.close(requisition.id);
		}
	}
}
